import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { FLAGS, flagFor } from "@/lib/flags";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const nav = useNavigate();
  const { login, register, error } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [country, setCountry] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const ok = isLogin
      ? await login(email, password)
      : await register(email, password, name, country || null);
    setLoading(false);
    if (ok) nav("/app");
  };

  const pickedFlag = flagFor(country);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b0e14] p-4" data-testid="auth-page">
      <div className="absolute inset-0 trading-grid opacity-40"/>
      <div className="relative bg-[#11151d] border border-slate-800 rounded-2xl w-full max-w-md p-8 shadow-2xl">
        <div className="mb-6 text-center">
          <div className="inline-flex w-16 h-16 rounded-xl items-center justify-center mb-3 overflow-hidden border border-slate-700 bg-[#0b0e14]" data-testid="auth-logo">
            <img src="/brand/logo.jpeg" alt="Institutional Sandbox" className="w-full h-full object-cover"/>
          </div>
          <h1 className="text-xl font-bold text-white font-mono tracking-tight">Institutional Sandbox</h1>
          <p className="text-xs text-slate-400 mt-1">{isLogin ? "Authenticate to access institutional matrix" : "Provision a new trader profile"}</p>
        </div>
        <form onSubmit={submit} className="space-y-3">
          {!isLogin && (
            <>
              <label className="block">
                <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Username</span>
                <input data-testid="register-name" required value={name} onChange={e => setName(e.target.value)}
                  placeholder="e.g. LiquidityHunter"
                  className="mt-1 w-full px-3 py-2.5 bg-[#161d28] border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500 font-mono"/>
              </label>
            </>
          )}
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{isLogin ? "Email" : "Gmail"}</span>
            <input data-testid="auth-email" type="email" required value={email} onChange={e => setEmail(e.target.value)}
              placeholder={isLogin ? "trader@example.com" : "yourname@gmail.com"}
              className="mt-1 w-full px-3 py-2.5 bg-[#161d28] border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500 font-mono"/>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Password</span>
            <input data-testid="auth-password" type="password" required value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-1 w-full px-3 py-2.5 bg-[#161d28] border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500 font-mono"/>
          </label>
          {!isLogin && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                National Flag <span className="text-slate-600 normal-case">(optional)</span>
              </span>
              <div className="mt-1 relative">
                <select data-testid="register-country" value={country} onChange={e => setCountry(e.target.value)}
                  className="w-full appearance-none pl-9 pr-8 py-2.5 bg-[#161d28] border border-slate-700 rounded-lg text-sm text-white focus:outline-none focus:border-blue-500 font-mono cursor-pointer">
                  <option value="">— No flag —</option>
                  {FLAGS.map(f => (
                    <option key={f.code} value={f.code}>{f.flag}  {f.name}</option>
                  ))}
                </select>
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base">
                  {pickedFlag ? pickedFlag.flag : <i className="fa-solid fa-flag text-slate-500 text-xs"/>}
                </span>
                <i className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 fa-solid fa-chevron-down text-slate-500 text-xs"/>
              </div>
            </label>
          )}
          {error && <div data-testid="auth-error" className="text-rose-400 text-xs bg-rose-500/10 border border-rose-500/20 rounded px-3 py-2">{error}</div>}
          <button data-testid="auth-submit" disabled={loading}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-bold text-sm transition-all disabled:opacity-50">
            {loading ? "..." : (isLogin ? "ENTER MATRIX" : "PROVISION ACCOUNT")}
          </button>
        </form>
        <div className="mt-4 text-center text-xs text-slate-500">
          {isLogin ? (
            <>No profile? <Link to="/register" data-testid="link-register" className="text-blue-400 hover:underline">Register</Link></>
          ) : (
            <>Already provisioned? <Link to="/login" data-testid="link-login" className="text-blue-400 hover:underline">Sign in</Link></>
          )}
        </div>
        {isLogin && (
          <div className="mt-4 text-center text-[10px] text-slate-600 font-mono">
            <Link to="/" className="hover:text-slate-400">← back to homepage</Link>
          </div>
        )}
      </div>
    </div>
  );
}
